import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import {
  GaugeIcon, RankingIcon, CurrencyCircleDollarIcon, BrainIcon, UploadSimpleIcon, RobotIcon, BroadcastIcon, RocketLaunchIcon, ListIcon, XIcon, TrophyIcon, PulseIcon, AtomIcon, ClockClockwiseIcon, SlidersIcon, StorefrontIcon, SquaresFourIcon,
} from "@phosphor-icons/react";

// Small QF robot mark, charcoal on the electric-green badge
function RobotMark({ size = 16 }) {
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} aria-hidden="true">
      <g stroke="#0B0C10" strokeWidth="3.4" strokeLinecap="round" fill="none">
        <line x1="21" y1="8"  x2="21" y2="18"/>
        <line x1="43" y1="8"  x2="43" y2="18"/>
      </g>
      <circle cx="21" cy="7" r="2.6" fill="#0B0C10"/>
      <circle cx="43" cy="7" r="2.6" fill="#0B0C10"/>
      <rect x="12" y="18" width="40" height="32" rx="9" fill="none" stroke="#0B0C10" strokeWidth="3.4"/>
      <circle cx="26" cy="33" r="3.2" fill="#0B0C10"/>
      <circle cx="38" cy="33" r="3.2" fill="#0B0C10"/>
      <path d="M25 40 Q32 46 39 40" fill="none" stroke="#0B0C10" strokeWidth="2.8" strokeLinecap="round"/>
      <rect x="6"  y="28" width="5" height="12" rx="2.2" fill="#0B0C10"/>
      <rect x="53" y="28" width="5" height="12" rx="2.2" fill="#0B0C10"/>
      <rect x="24" y="50" width="16" height="5" rx="1.8" fill="#0B0C10"/>
    </svg>
  );
}

const items = [
  { to: "/admin", label: "Overview", icon: GaugeIcon, testid: "nav-overview" },
  { to: "/leaderboard", label: "Leaderboard", icon: RankingIcon, testid: "nav-leaderboard" },
  { to: "/symbol-leaders", label: "Symbol Leaders", icon: CurrencyCircleDollarIcon, testid: "nav-symbol-leaders" },
  { to: "/strategy-leaders", label: "Strategy Leaders", icon: AtomIcon, testid: "nav-strategy-leaders" },
  { to: "/hall-of-fame", label: "Hall of Fame", icon: TrophyIcon, testid: "nav-hall-of-fame" },
  { to: "/live", label: "Live Trading", icon: PulseIcon, testid: "nav-live" },
  { to: "/candidates", label: "Deploy Candidates", icon: RocketLaunchIcon, testid: "nav-candidates" },
  { to: "/admin/portfolios", label: "Portfolio Preview", icon: SquaresFourIcon, testid: "nav-portfolios" },
  { to: "/symbols", label: "Symbols", icon: CurrencyCircleDollarIcon, testid: "nav-symbols" },
  { to: "/heatmap", label: "Fleet Heatmap", icon: AtomIcon, testid: "nav-heatmap" },
  { to: "/store", label: "Store (public)", icon: StorefrontIcon, testid: "nav-store", external: true },
  { to: "/decisions", label: "AI Decisions", icon: BrainIcon, testid: "nav-decisions" },
  { to: "/admin/brain", label: "Brain Insights", icon: BrainIcon, testid: "nav-brain-insights" },
  { to: "/admin/morning-report", label: "Morning Report", icon: BrainIcon, testid: "nav-morning-report" },
  { to: "/backtest-log", label: "Backtest Log", icon: ClockClockwiseIcon, testid: "nav-backtest-log" },
  { to: "/config/seed-symbols", label: "Seed Symbols", icon: SlidersIcon, testid: "nav-seed-symbols" },
  { to: "/bridge", label: "Bridge", icon: BroadcastIcon, testid: "nav-bridge" },
  { to: "/upload", label: "Upload Excel", icon: UploadSimpleIcon, testid: "nav-upload" },
];

function timeAgo(iso) {
  if (!iso) return "never";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function Sidebar() {
  const [status, setStatus] = useState({ connected: false, last_sync: null });
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const fetchStatus = () => api.get("/agent/status").then((r) => setStatus(r.data)).catch(() => {});
    fetchStatus();
    const t = setInterval(fetchStatus, 30000);
    return () => clearInterval(t);
  }, []);

  // Close drawer on navigation
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const currentLabel = items.find(it => it.to === location.pathname || (it.to !== "/" && location.pathname.startsWith(it.to)))?.label || "BotGenomix";

  return (
    <>
      {/* MOBILE TOP BAR */}
      <header className="md:hidden sticky top-0 z-40 flex items-center justify-between px-4 py-3 bg-[#0B0C10] border-b border-[#2A2D3A]">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-[#D6FF38] flex items-center justify-center accent-glow">
            <RobotMark size={18} />
          </div>
          <div>
            <div className="text-[14px] font-semibold tracking-tight leading-none">BotGenomix</div>
            <div className="text-[9px] mono uppercase tracking-widest text-[#64748B] mt-0.5">{currentLabel}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className={status.connected ? "pulse-dot" : "w-2 h-2 rounded-full bg-[#64748B] inline-block"} />
            <span className="text-[10px] mono uppercase tracking-widest text-[#94A3B8]">{status.connected ? "LIVE" : "OFF"}</span>
          </div>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setMobileOpen(true)}
            className="w-9 h-9 rounded-md border border-[#2A2D3A] flex items-center justify-center text-[#F1F5F9] hover:border-[#D6FF38]/40"
            aria-label="Open menu"
          >
            <ListIcon size={18} />
          </button>
        </div>
      </header>

      {/* MOBILE DRAWER BACKDROP */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* SIDEBAR (desktop static / mobile drawer) */}
      <aside
        data-testid="sidebar"
        className={`fixed md:sticky top-0 left-0 z-50 w-60 shrink-0 border-r border-[#2A2D3A] bg-[#0B0C10] h-screen flex flex-col transition-transform duration-200 md:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
      >
        <div className="px-5 py-6 border-b border-[#2A2D3A] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-[#D6FF38] flex items-center justify-center accent-glow">
              <RobotMark size={18} />
            </div>
            <div>
              <div className="text-[15px] font-semibold tracking-tight">BotGenomix</div>
              <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">AI Bot Lab</div>
            </div>
          </div>
          <button
            onClick={() => setMobileOpen(false)}
            className="md:hidden text-[#94A3B8] hover:text-[#F1F5F9]"
            aria-label="Close menu"
          >
            <XIcon size={18} />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                data-testid={it.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                    isActive
                      ? "bg-[#15171E] text-[#D6FF38] border border-[#2A2D3A]"
                      : "text-[#94A3B8] hover:text-[#F1F5F9] hover:bg-[#15171E] border border-transparent"
                  }`
                }
              >
                <Icon size={18} weight="duotone" />
                <span>{it.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="px-4 py-4 border-t border-[#2A2D3A]" data-testid="bridge-status">
          <div className="flex items-center gap-2 text-xs mono uppercase tracking-widest text-[#64748B]">
            <span className={status.connected ? "pulse-dot" : "w-2 h-2 rounded-full bg-[#64748B] inline-block"} />
            {status.connected ? "Live · Bridge" : "Bridge Offline"}
          </div>
          <div className="mt-2 flex items-center gap-2 text-[11px] text-[#94A3B8]">
            <RobotIcon size={14} />
            {status.connected ? `synced ${timeAgo(status.last_sync)}` : "Demo data"}
          </div>
        </div>
      </aside>
    </>
  );
}
